import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as ModuleIcon,
  FolderOpen as FeatureIcon,
  Description as SubFeatureIcon,
  Label as SectionIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsAPI } from '../../shared/services/api';
import toast from 'react-hot-toast';

// Import dialog components
import PermissionModuleDialog from '../dialogs/PermissionModuleDialog';
import FeatureDialog from '../dialogs/FeatureDialog';
import SubFeatureDialog from '../dialogs/SubFeatureDialog';
import SectionDialog from '../dialogs/SectionDialog';

const PermissionManagement = () => {
  const queryClient = useQueryClient();
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [selectedPortal, setSelectedPortal] = useState('all');

  // Portal configuration for display
  const portalConfig = {
    admin: { name: 'Admin', color: '#2563eb' },
    member: { name: 'Member', color: '#10b981' },
    corporate: { name: 'Corporate', color: '#8b5cf6' },
    provider: { name: 'Provider', color: '#f59e0b' },
    intermediary: { name: 'Intermediary', color: '#7c3aed' },
    insurer: { name: 'Insurer', color: '#ef4444' },
    irda: { name: 'IRDA', color: '#06b6d4' }
  };

  // Dialog states
  const [moduleDialog, setModuleDialog] = useState({ open: false, module: null, isEdit: false });
  const [featureDialog, setFeatureDialog] = useState({ open: false, permissionId: null, feature: null, featureId: null, isEdit: false });
  const [subFeatureDialog, setSubFeatureDialog] = useState({ open: false, permissionId: null, featureId: null, subFeature: null, subFeatureId: null, isEdit: false });
  const [sectionDialog, setSectionDialog] = useState({ open: false, permissionId: null, featureId: null, subFeatureId: null, section: null, sectionId: null, isEdit: false });

  // Fetch all permissions
  const { data: permissionsData, isLoading, error, refetch } = useQuery({
    queryKey: ['permissions', selectedPortal],
    queryFn: () => permissionsAPI.getPermissions(selectedPortal === 'all' ? {} : { portal: selectedPortal }),
  });

  // Delete permission mutation
  const deletePermissionMutation = useMutation({
    mutationFn: permissionsAPI.deletePermission,
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Permission deleted successfully');
    },
    onError: (error) => {
      console.error('Delete permission error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete permission';
      toast.error(errorMessage);
    },
  });

  // Delete feature mutation
  const deleteFeatureMutation = useMutation({
    mutationFn: ({ permissionId, featureId }) =>
      permissionsAPI.deleteFeature(permissionId, featureId),
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Feature deleted successfully');
    },
    onError: (error) => {
      console.error('Delete feature error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete feature';
      toast.error(errorMessage);
    },
  });

  // Delete sub-feature mutation
  const deleteSubFeatureMutation = useMutation({
    mutationFn: ({ permissionId, featureId, subFeatureId }) => 
      permissionsAPI.deleteSubFeature(permissionId, featureId, subFeatureId),
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Sub-feature deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete sub-feature');
    },
  });

  // Delete section mutation
  const deleteSectionMutation = useMutation({
    mutationFn: ({ permissionId, featureId, subFeatureId, sectionId }) => 
      permissionsAPI.deleteSection(permissionId, featureId, subFeatureId, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Section deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete section');
    },
  });

  const handleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleDeletePermission = (permissionId) => {
    if (window.confirm('Are you sure you want to delete this permission module? This action cannot be undone.')) {
      deletePermissionMutation.mutate(permissionId);
    }
  };

  const handleDeleteFeature = (permissionId, featureId) => {
    if (window.confirm('Are you sure you want to delete this feature? This action cannot be undone.')) {
      deleteFeatureMutation.mutate({ permissionId, featureId });
    }
  };

  const handleDeleteSubFeature = (permissionId, featureId, subFeatureId) => {
    if (window.confirm('Are you sure you want to delete this sub-feature? This action cannot be undone.')) {
      deleteSubFeatureMutation.mutate({ permissionId, featureId, subFeatureId });
    }
  };

  const handleDeleteSection = (permissionId, featureId, subFeatureId, sectionId) => {
    if (window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      deleteSectionMutation.mutate({ permissionId, featureId, subFeatureId, sectionId });
    }
  };

  // Dialog handlers
  const openModuleDialog = (module = null, isEdit = false) => {
    setModuleDialog({ open: true, module, isEdit });
  };

  const closeModuleDialog = () => {
    setModuleDialog({ open: false, module: null, isEdit: false });
  };

  const openFeatureDialog = (permissionId, feature = null, featureId = null, isEdit = false) => {
    setFeatureDialog({ open: true, permissionId, feature, featureId, isEdit });
  };

  const closeFeatureDialog = () => {
    setFeatureDialog({ open: false, permissionId: null, feature: null, featureId: null, isEdit: false });
  };

  const openSubFeatureDialog = (permissionId, featureId, subFeature = null, subFeatureId = null, isEdit = false) => {
    setSubFeatureDialog({ open: true, permissionId, featureId, subFeature, subFeatureId, isEdit });
  };

  const closeSubFeatureDialog = () => {
    setSubFeatureDialog({ open: false, permissionId: null, featureId: null, subFeature: null, subFeatureId: null, isEdit: false });
  };

  const openSectionDialog = (permissionId, featureId, subFeatureId, section = null, sectionId = null, isEdit = false) => {
    setSectionDialog({ open: true, permissionId, featureId, subFeatureId, section, sectionId, isEdit });
  };

  const closeSectionDialog = () => {
    setSectionDialog({ open: false, permissionId: null, featureId: null, subFeatureId: null, section: null, sectionId: null, isEdit: false });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" sx={{width: '75vw'}}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading permissions...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load permissions: {error.message}
      </Alert>
    );
  }

  const permissions = permissionsData?.data?.data || [];
  return (
    <Box sx={{
      width: '85vw',
      maxWidth: '100%',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 3, // Reduced from 4
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: 2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600, // Reduced from 700
              color: '#1e293b',
              mb: 0.5, // Reduced from 1
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '1.5rem' // More compact
            }}
          >
            Permission Management
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#64748b',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '0.875rem' // Reduced from 1rem
            }}
          >
            Manage hierarchical permissions: Modules → Features → Sub-features → Sections
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 2,
          flexShrink: 0
        }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc'
              }
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openModuleDialog()}
            sx={{
              bgcolor: '#2563eb',
              '&:hover': {
                bgcolor: '#1d4ed8'
              }
            }}
          >
            Add Module
          </Button>
        </Box>
      </Box>

      {/* Portal Filter */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b', minWidth: 'fit-content' }}>
                Filter by Portal:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Portal</InputLabel>
                <Select
                  value={selectedPortal}
                  label="Portal"
                  onChange={(e) => setSelectedPortal(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#cbd5e1',
                    },
                  }}
                >
                  <MenuItem value="all">All Portals</MenuItem>
                  {Object.entries(portalConfig).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: config.color
                          }}
                        />
                        {config.name} Portal
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedPortal !== 'all' && (
                <Chip
                  label={`Showing: ${portalConfig[selectedPortal]?.name || selectedPortal} Portal`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onDelete={() => setSelectedPortal('all')}
                  sx={{
                    borderColor: portalConfig[selectedPortal]?.color,
                    color: portalConfig[selectedPortal]?.color
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Permission Structure */}
      <Grid container spacing={3} columns={12}>
        <Grid size={{xs:12, md: 12, sm: 12}}>
          <Card
            elevation={0}
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              bgcolor: '#ffffff'
            }}
          >
            <CardHeader
              title="Permission Structure"
              subheader={`${permissions.length} permission modules configured`}
              titleTypographyProps={{
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: 600,
                color: '#1e293b'
              }}
              subheaderTypographyProps={{
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                color: '#64748b'
              }}
              sx={{ pb: 2 }}
            />
            <CardContent sx={{ pt: 0 }}>
              {permissions.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No permissions configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create your first permission module to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openModuleDialog()}
                  >
                    Create Permission Module
                  </Button>
                </Box>
              ) : (
                <Box>
                  {permissions.map((permission) => (
                    <Accordion
                      key={permission._id}
                      expanded={expandedModules[permission._id] || false}
                      onChange={() => handleModuleExpand(permission._id)}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <ModuleIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6">
                              {permission.displayName}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={`${permission.features?.length || 0} features`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`${permission.totalSections || 0} sections`}
                              size="small"
                              variant="outlined"
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {permission.portalAccess?.map((portal) => (
                                <Chip
                                  key={portal}
                                  label={portalConfig[portal]?.name || portal}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: portalConfig[portal]?.color || '#64748b',
                                    color: portalConfig[portal]?.color || '#64748b',
                                    '&:hover': {
                                      backgroundColor: `${portalConfig[portal]?.color || '#64748b'}10`
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                            <IconButton
                              component="div"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openModuleDialog(permission, true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              component="div"
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePermission(permission._id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Features */}
                        {permission.features?.map((feature) => (
                          <Box key={feature._id} sx={{ ml: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <FeatureIcon sx={{ mr: 1, color: 'secondary.main' }} />
                              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                {feature.displayName}
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => openSubFeatureDialog(permission._id, feature._id)}
                                sx={{ mr: 1 }}
                              >
                                Add Sub-feature
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => openFeatureDialog(permission._id, feature, feature._id, true)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteFeature(permission._id, feature._id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                            
                            {/* Sub-features */}
                            {feature.subFeatures?.map((subFeature) => (
                              <Box key={subFeature._id} sx={{ ml: 4, mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <SubFeatureIcon sx={{ mr: 1, color: 'info.main' }} />
                                  <Typography variant="body1" sx={{ flexGrow: 1 }}>
                                    {subFeature.displayName}
                                  </Typography>
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => openSectionDialog(permission._id, feature._id, subFeature._id)}
                                    sx={{ mr: 1 }}
                                  >
                                    Add Section
                                  </Button>
                                  <IconButton
                                    size="small"
                                    onClick={() => openSubFeatureDialog(permission._id, feature._id, subFeature, subFeature._id, true)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteSubFeature(permission._id, feature._id, subFeature._id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                
                                {/* Sections */}
                                <List dense sx={{ ml: 2 }}>
                                  {subFeature.sections?.map((section) => (
                                    <ListItem key={section._id}>
                                      <SectionIcon sx={{ mr: 1, color: 'success.main' }} />
                                      <ListItemText
                                        primary={section.displayName}
                                      />
                                      <ListItemSecondaryAction>
                                        <IconButton
                                          size="small"
                                          onClick={() => openSectionDialog(permission._id, feature._id, subFeature._id, section, section._id, true)}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeleteSection(permission._id, feature._id, subFeature._id, section._id)}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                  ))}
                                  {(!subFeature.sections || subFeature.sections.length === 0) && (
                                    <ListItem>
                                      <ListItemText
                                        primary="No sections configured"
                                        secondary="Add sections to this sub-feature"
                                      />
                                    </ListItem>
                                  )}
                                </List>
                              </Box>
                            ))}
                            
                            {(!feature.subFeatures || feature.subFeatures.length === 0) && (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                                No sub-features configured
                              </Typography>
                            )}
                            <Divider sx={{ mt: 1 }} />
                          </Box>
                        ))}
                        
                        {(!permission.features || permission.features.length === 0) && (
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            No features configured
                          </Typography>
                        )}
                        
                        <Box sx={{ mt: 2, ml: 2 }}>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => openFeatureDialog(permission._id)}
                          >
                            Add Feature
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <PermissionModuleDialog
        open={moduleDialog.open}
        onClose={closeModuleDialog}
        module={moduleDialog.module}
        isEdit={moduleDialog.isEdit}
      />

      <FeatureDialog
        open={featureDialog.open}
        onClose={closeFeatureDialog}
        permissionId={featureDialog.permissionId}
        feature={featureDialog.feature}
        featureId={featureDialog.featureId}
        isEdit={featureDialog.isEdit}
      />

      <SubFeatureDialog
        open={subFeatureDialog.open}
        onClose={closeSubFeatureDialog}
        permissionId={subFeatureDialog.permissionId}
        featureId={subFeatureDialog.featureId}
        subFeature={subFeatureDialog.subFeature}
        subFeatureId={subFeatureDialog.subFeatureId}
        isEdit={subFeatureDialog.isEdit}
      />

      <SectionDialog
        open={sectionDialog.open}
        onClose={closeSectionDialog}
        permissionId={sectionDialog.permissionId}
        featureId={sectionDialog.featureId}
        subFeatureId={sectionDialog.subFeatureId}
        section={sectionDialog.section}
        sectionId={sectionDialog.sectionId}
        isEdit={sectionDialog.isEdit}
      />
    </Box>
  );
};

export default PermissionManagement;
